import React from 'react';
import { SQUAD_MAX, SQUAD_ABILITIES, canUseAbility } from '../utils/squad';

// Cohesion is the Kill-team's shared resource: it powers Squad Mode abilities
// and is drained by casualties, fear and failed leadership. The bar colour is
// the at-a-glance warning — emerald while there is room to spend, amber once
// the team is down to its last couple of abilities, rose when Squad Mode is
// one bad roll away from breaking.
function cohesionTone(cohesion) {
  if (cohesion > 5) return { bar: 'bg-emerald-500', text: 'text-emerald-300' };
  if (cohesion >= 3) return { bar: 'bg-amber-500', text: 'text-amber-300' };
  return { bar: 'bg-rose-500', text: 'text-rose-300' };
}

const EVENT_TONE = {
  damage: 'text-rose-300',
  ability: 'text-amber-300',
  abilityBlocked: 'text-slate-400',
  regroup: 'text-emerald-300',
  squadModeLost: 'text-rose-400 font-bold'
};

// squad.js builds its events without timestamps (it is pure logic and owns no
// clock). The roller stamps them on the way into state, so fall back to the
// sequence number for anything that arrives unstamped.
function eventStamp(ev, idx) {
  if (!ev || ev.ts == null) return `#${idx + 1}`;
  return new Date(ev.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function eventText(ev) {
  if (!ev) return '';
  switch (ev.type) {
    case 'damage':
      return `−${ev.amount} Cohesion — ${ev.reason} (now ${ev.cohesion})`;
    case 'ability':
      return `${ev.ability} — spent ${ev.cost} Cohesion (now ${ev.cohesion})`;
    case 'abilityBlocked':
      return `blocked: ${ev.reason}`;
    case 'regroup':
      return `Regrouped +${ev.gain} Cohesion (now ${ev.cohesion})`;
    case 'squadModeLost':
      return `SQUAD MODE LOST — ${ev.reason}`;
    default:
      return ev.reason || ev.type || '';
  }
}

// Displays and drives Cohesion / Squad Mode. All of the rules live in
// src/utils/squad.js; this component only renders state and reports intent
// back to the roller, which owns the dice.
export default function SquadPanel({ squadState, setSquadState, onRegroup, onFear, onAbility, lastCombatResult }) {
  if (!squadState) return null;
  const { cohesion, inSquadMode, leader } = squadState;
  const tone = cohesionTone(cohesion);
  const pct = Math.max(0, Math.min(100, (cohesion / SQUAD_MAX) * 100));
  const events = Array.isArray(squadState.events) ? squadState.events : [];

  return (
    <div className="mt-3 rounded-xl bg-slate-900 border border-slate-700 p-4 space-y-3">
      <div className="flex items-center gap-3">
        <span className="text-xs uppercase tracking-wide text-slate-400">Cohesion</span>
        <div className="flex-1 h-2.5 rounded-full bg-slate-800 overflow-hidden">
          <div className={`h-full ${tone.bar} transition-all`} style={{ width: `${pct}%` }} />
        </div>
        <span className={`text-sm font-semibold ${tone.text}`}>{cohesion}/{SQUAD_MAX}</span>
        {inSquadMode ? (
          <span className="text-xs px-2 py-1 rounded bg-emerald-700 text-emerald-100 font-bold animate-pulse">SQUAD MODE ACTIVE</span>
        ) : (
          <span className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-500 font-semibold">Squad Mode lost</span>
        )}
      </div>
      <div className="text-xs text-slate-500">Leader: <span className="text-slate-300">{leader}</span></div>

      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-slate-400">Squad Mode Abilities</div>
        <div className="grid gap-2 sm:grid-cols-3">
          {SQUAD_ABILITIES.map((ability, idx) => {
            const usable = canUseAbility(squadState, ability);
            return (
              <button
                key={ability.id}
                type="button"
                disabled={!usable}
                onClick={() => onAbility(idx)}
                title={usable ? ability.blurb : (inSquadMode ? 'Not enough Cohesion' : 'Not in Squad Mode')}
                className="text-left rounded-xl border border-slate-700 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:hover:bg-slate-800 px-3 py-2 transition"
              >
                <div className="text-sm font-semibold text-slate-100">{ability.name}</div>
                <div className="text-xs text-amber-300">&minus;{ability.cost} Cohesion</div>
                <div className="text-xs text-slate-400 mt-1">{ability.blurb}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={cohesion >= SQUAD_MAX}
          onClick={onRegroup}
          title="The leader makes a Command test to pull the squad back together"
          className="rounded-xl px-3 py-1.5 text-sm bg-emerald-800 hover:bg-emerald-700 disabled:opacity-40"
        >
          Regroup (Command test)
        </button>
        {lastCombatResult && (
          <button
            type="button"
            onClick={() => onFear(0)}
            title="Roll a Fear test for the squad; failure costs Cohesion"
            className="rounded-xl px-3 py-1.5 text-sm bg-rose-900 hover:bg-rose-800"
          >
            Call Fear Test
          </button>
        )}
      </div>

      {events.length > 0 && (
        <div className="max-h-32 overflow-auto rounded-lg bg-slate-950/60 border border-slate-800 p-2 space-y-1">
          {events.map((ev, idx) => (
            <div key={`${idx}-${ev.type}`} className="text-xs flex gap-2">
              <span className="text-slate-600 shrink-0">{eventStamp(ev, idx)}</span>
              <span className={EVENT_TONE[ev.type] || 'text-slate-300'}>{eventText(ev)}</span>
            </div>
          )).reverse()}
        </div>
      )}
    </div>
  );
}
